import { Text, Tooltip, Box } from '@mantine/core';
import {
  getAttendanceCoverageDisplay,
  UNRECORDED_LABEL,
  type AttendanceCoverageInput,
} from '../../utils/attendanceCoverage';

/**
 * MED-1266 — the single shared way to render an attendance rate with its marking coverage.
 *
 * Rules (identical on every surface, driven by getAttendanceCoverageDisplay):
 *   - un-recorded (no recorded outcomes) → an explicit "Not recorded" state, never 0% (AC13)
 *   - incomplete coverage → the rate PLUS a visible "N of M marked" annotation, no hover
 *     required (AC10); a tooltip may carry the longer detail but is never the only signal
 *   - complete coverage → just the rate, nothing added (AC11)
 *
 * Variants:
 *   - 'inline'  → "60% · 5 of 6 marked" on one line (stat rows, list items)
 *   - 'stacked' → big rate with the coverage annotation on a muted line below (stat boxes)
 *   - 'compact' → "60%" with a small marker + tooltip (tight table cells); the marker is the
 *                 always-visible incompleteness signal, the tooltip only carries the detail
 */
export interface AttendanceRateProps extends AttendanceCoverageInput {
  variant?: 'inline' | 'stacked' | 'compact';
  /** Font size for the rate value (Mantine size token or px string). */
  size?: string;
  /** Font weight for the rate value. */
  fw?: number;
}

export function AttendanceRate({
  rate,
  marked,
  population,
  variant = 'inline',
  size = 'sm',
  fw,
}: AttendanceRateProps) {
  const d = getAttendanceCoverageDisplay({ rate, marked, population });

  if (d.isUnrecorded) {
    return (
      <Tooltip label="No attendance has been recorded yet" withArrow>
        <Text component="span" size={size} c="dimmed" fs="italic">
          {UNRECORDED_LABEL}
        </Text>
      </Tooltip>
    );
  }

  if (variant === 'stacked') {
    return (
      <Box>
        <Text size={size} fw={fw} lh={1}>
          {d.rateLabel}
        </Text>
        {!d.isComplete && (
          <Text size="10px" c="dimmed" mt={2}>
            {d.coverageLabel}
          </Text>
        )}
      </Box>
    );
  }

  if (variant === 'compact') {
    // Tight surface: the rate plus a small always-visible marker; tooltip carries detail.
    return (
      <Text component="span" size={size} fw={fw}>
        {d.rateLabel}
        {!d.isComplete && (
          <Tooltip label={d.detailLabel} withArrow>
            <Text component="span" c="dimmed" ml={4} style={{ cursor: 'help' }}>
              ({d.coverageLabel})
            </Text>
          </Tooltip>
        )}
      </Text>
    );
  }

  // inline (default)
  return (
    <Text component="span" size={size} fw={fw}>
      {d.rateLabel}
      {!d.isComplete && (
        <Text component="span" c="dimmed">
          {' · '}
          {d.coverageLabel}
        </Text>
      )}
    </Text>
  );
}
